import { useEffect, useState } from "react";
import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButtonWithHover";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { useThunk } from "../../hooks/useThunk";
import { updatePassword, validateToken } from "../../store";
import showToast from "../../utils/toastNotifications";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { Eye, EyeOff } from "lucide-react";

const SetNewPasswordForm = () => {
  const navigate = useNavigate();
  const [linkVerified, setLinkVerified] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [doVerifyPasswordForgot, isVerifying] = useThunk(validateToken);
  const [doChangePassword, isPasswordChanging] = useThunk(updatePassword);
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      password: "",
      confirmPassword: "",
    },
    {
      password: true,
      confirmPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true,
    });

    if (validateForm()) {
      const result = await doChangePassword({ token, id, data: formData });

      if (result.success) {
        navigate("/signin", { replace: true });
        showToast("success", result.response?.message);
      } else {
        showToast("error", result.error?.message);
      }
    }
  };

  useEffect(() => {
    if (!token || !id) {
      setLinkVerified(false);
      setVerifyError(errorMessages.FAILED_RESET_PASSWORD);
      return;
    }
    doVerifyPasswordForgot({ id, token }).then((result) => {
      if (result?.success) {
        setLinkVerified(true);
      } else {
        setLinkVerified(false);
        setVerifyError(result?.error?.message);
      }
    });
  }, [id, token, doVerifyPasswordForgot]);

  const handleOkayButton = () => {
    navigate("/forgot-password", { replace: true });
  };

  return (
    <div className="relative h-[550px] w-full my-auto flex flex-col p-2 lg:p-6">
      <h1 className="text-level-4 md:text-level-3 lg:text-level-2 xl2:text-[48px] font-semibold font-poppins">
        <span className="text-black">Set New </span>
        <span className="text-roselea-green-1">Password</span>
      </h1>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col justify-center items-center"
      >
        <div className="flex flex-col gap-3 mt-[40px]">
          <div className="relative">
            <CustomInput
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              placeholder="New Password"
              error={touched.password && errors.password}
              onBlur={() => handleBlur("password", formData.password)}
              onFocus={() => clearError("password")}
              onChange={(e) => handleInputChange("password", e.target.value)}
              className="w-[280px] sm:w-[380px] lg:w-[400px] xl2:w-[512px] h-[40px] px-3 rounded-[12px] border border-input-border-roselea-green bg-primary-light/30 text-gray-600"
              labelStyle="font-poppins font-medium  sm:text-[16px] text-dark"
              inputStyle="bg-input-roselea-green bg-opacity-[0.03]"
              isRequired={true}
            />
            <button
              type="button"
              className="absolute right-4 top-[20px] -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="relative">
            <CustomInput
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              placeholder="Re-Enter Password"
              error={touched.confirmPassword && errors.confirmPassword}
              onBlur={() =>
                handleBlur("confirmPassword", formData.confirmPassword)
              }
              onFocus={() => clearError("confirmPassword")}
              onChange={(e) =>
                handleInputChange("confirmPassword", e.target.value)
              }
              className="w-[280px] sm:w-[380px] lg:w-[400px] xl2:w-[512px] h-[40px] px-3 rounded-[12px] border border-input-border-roselea-green bg-primary-light/30 text-gray-600"
              labelStyle="font-poppins font-medium  sm:text-[16px] text-dark"
              inputStyle="bg-input-roselea-green bg-opacity-[0.03]"
              isRequired={true}
            />
            <button
              type="button"
              className="absolute right-4 top-[20px] -translate-y-1/2 text-gray-500"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2 justify-center items-center">
          <CustomButton
            type="submit"
            loading={isPasswordChanging}
            className="h-[40px] xl2:h-[60px] w-[280px] sm:w-[380px] lg:w-[400px] xl2:w-[512px] text-level-6 xl2:text-[20px] text-white font-poppins font-semibold bg-roselea-green-1 mt-[50px] rounded-xl"
            buttonText="Reset Password"
          />
          <Link to="/signin">
            <span className="text-roselea-green-1 font-alexandria sm:text-[16px] cursor-pointer underline hover:no-underline">
              Back to Login
            </span>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default SetNewPasswordForm;
